/**
 * Classifying a failed tender-detail fetch — and deciding whether to try again.
 *
 * The detail page used to render ONE error block for every rejection: «Тендер
 * не найден. Возможно, он был удалён или ссылка устарела.» So a dropped
 * connection, a 499 on navigating away, or a 5xx mid-deploy all told a paying
 * reader the lot had been DELETED, while the catalogue card they arrived from
 * still showed it as «Активен».
 *
 * Observed on prod 2026-09-09, tender `cmttt6zbn2k9kk20nz3otmc1r` (Asakabank,
 * live, deadline 16.09.2026): one request never produced a response, the reader
 * got the deletion text, and eleven seconds later the same URL loaded fine.
 *
 * That this is common rather than theoretical is measured next door, in
 * {@link NetworkError}'s doc block: `/api/catalog` alone logged 13
 * client-aborted requests (nginx 499) in a single day, against 99% mobile
 * traffic.
 *
 * Kept as pure functions OUTSIDE the component so the branch is testable
 * without rendering a page that mounts a dozen self-fetching panels.
 */
import { ApiError, NetworkError } from './api';

/**
 * Which of the two stories the page should tell.
 *
 * - `not_found` — the server answered, definitively, that this lot is gone.
 *   The ONLY case allowed to say «удалён или ссылка устарела».
 * - `network` — the request never produced an answer. Nothing is settled.
 * - `http` — the server answered with a failure that is not a 404.
 *
 * `network` and `http` share one user-facing message, because the only
 * distinction a READER can act on is "gone" vs "try again". They stay separate
 * here because the distinction WE act on is in the event: a connectivity
 * problem and a broken deploy have different fixes.
 */
export type TenderErrorKind = 'not_found' | 'network' | 'http';

/** The API's machine-readable code for a lot that genuinely is not there
 *  (`catalog.service.ts`). Checked alongside the status because a code is
 *  stable across any future status change. */
const NOT_FOUND_CODE = 'tender_not_found';

/**
 * The generic client throws a bare `Error('HTTP <status>')` when the failing
 * response carried no error envelope to parse (`handleResponse` in api.ts).
 * That is exactly the shape of an nginx 502/504 during a deploy — an HTML error
 * page, not JSON — so this regex is not an edge case, it is the main 5xx path.
 * Mirrors `failureReason()` in auth-context.tsx, which learned the same lesson.
 */
function bareHttpStatus(err: unknown): number | null {
  if (!(err instanceof Error)) return null;
  const m = /^HTTP (\d{3})$/.exec(err.message);
  return m ? Number(m[1]) : null;
}

/** The HTTP status behind a rejection, when there is one. */
export function tenderErrorStatus(err: unknown): number | null {
  if (err instanceof ApiError && err.status != null) return err.status;
  return bareHttpStatus(err);
}

/** Which error block the detail page should render. */
export function tenderErrorKind(err: unknown): TenderErrorKind {
  // A NetworkError never reached a server, so it can never carry a verdict —
  // check it first and unconditionally.
  if (err instanceof NetworkError) return 'network';
  if (err instanceof ApiError && err.code === NOT_FOUND_CODE) return 'not_found';
  const status = tenderErrorStatus(err);
  if (status === 404) return 'not_found';
  if (status != null) return 'http';
  // Residue: none of our classes, no parsable status. `fetchTender` only
  // rejects through `apiRequest`, whose non-ApiError rejection is a
  // NetworkError by construction, so this is a shape we do not expect. Fall in
  // with `network` — mirroring `failureReason()`'s fallback, and because the
  // one thing an unrecognised error must NOT do is claim the lot was deleted.
  return 'network';
}

/**
 * The `reason` carried by `tender_load_failed` — `not_found`, `network`, or
 * `http_<status>`. Deliberately the same vocabulary `failureReason()` in
 * auth-context.tsx emits for `login_failed`/`register_failed`, so one query can
 * read failure reasons across both surfaces.
 */
export function tenderErrorReason(err: unknown): string {
  const kind = tenderErrorKind(err);
  if (kind !== 'http') return kind;
  return `http_${tenderErrorStatus(err)}`;
}

/**
 * `/catalog/:id` resilience, deliberately the same shape as `/auth/me`'s
 * (`ME_MAX_ATTEMPTS` / `ME_RETRY_BACKOFF_MS`): three attempts total, linear
 * back-off of attempt × base → 300ms then 600ms.
 */
export const TENDER_MAX_ATTEMPTS = 3;
export const TENDER_RETRY_BACKOFF_MS = 300;

/**
 * react-query's `retry` predicate.
 *
 * ⚠️ `failureCount` is 0-INDEXED on the first failure — query-core's retryer
 * calls `retry(failureCount, error)` and only THEN increments — so
 * `< MAX_ATTEMPTS - 1` yields exactly MAX_ATTEMPTS attempts (2 retries).
 *
 * `isServer` is a parameter with a live default rather than a module constant
 * so the prerender branch is testable without stubbing globals, and so the
 * value is read per call instead of frozen at import.
 */
export function shouldRetryTenderQuery(
  failureCount: number,
  err: unknown,
  isServer: boolean = typeof window === 'undefined',
): boolean {
  // SSR / prerender: never retry. `scripts/prerender.ts` renders ~5k
  // (locale, tender) pages in one CI job, and that job is what flips the live
  // symlink — up to 900ms of back-off per failing page would lengthen a
  // deploy to rescue a page the 3-hourly schedule regenerates anyway. The
  // codebase's SSR check is `typeof window === 'undefined'` (used in ~20
  // modules); `import.meta.env.SSR` is used nowhere.
  if (isServer) return false;
  if (failureCount >= TENDER_MAX_ATTEMPTS - 1) return false;

  const kind = tenderErrorKind(err);
  // The server has spoken and the lot is gone. Repeating the question wastes
  // the reader's time and cannot change the answer.
  if (kind === 'not_found') return false;
  // Nothing is settled — this is the case the whole change exists for.
  if (kind === 'network') return true;
  // A real HTTP answer: only a 5xx is worth repeating. 400/403/429 are
  // decisions rather than accidents, and retrying one only delays the message.
  const status = tenderErrorStatus(err);
  return status != null && status >= 500;
}

/** Linear back-off between attempts: 300ms, then 600ms. */
export function tenderRetryDelay(failureCount: number): number {
  return TENDER_RETRY_BACKOFF_MS * (failureCount + 1);
}
